import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { Dropbox, DropboxAuth } from 'dropbox';
import { parseQueryString } from '~/src/utils/windowLocationUtil';
import IBookmark from '~/src/types/IBookmark';
import { parseFile } from '~/src/service/file-service';
import { mergeBookmarks } from '~/src/utils/migration-util';
import Button from '../elements/button/Button';

const dboxRefreshToken = 'dbox-refresh-token';
const dboxCodeVerifier = 'dbox-code-verifier';
const clientId = 'u9bp7y0a2vxnh1q';

const redirectUrl =
  process.env.NODE_ENV === 'production'
    ? 'https://bookmarks.bha.ee'
    : 'http://localhost:1234';

// One shared DropboxAuth instance; tokens refresh automatically on every
// request made through a Dropbox instance created with `{ auth }`.
// Don't pass `fetch`: the unbound global throws "Illegal invocation" when the
// SDK calls it; the SDK binds window.fetch itself.
const dbxAuth = new DropboxAuth({ clientId });

const MainWrapper: React.FC<{
  className?: string;
  prefix?: boolean;
  children: ReactNode;
}> = ({ className, prefix, children }) => (
  <div className={className}>
    {prefix ? <>Dropbox:&nbsp;</> : null}
    {children}
  </div>
);

interface Props {
  className?: string;
  bookmarks: IBookmark[];
  onBookmarksLoad: React.Dispatch<React.SetStateAction<IBookmark[]>>;
}

export const Auth: React.FC<Props> = ({
  className,
  bookmarks,
  onBookmarksLoad,
}) => {
  const [dbx, setDbx] = useState<Dropbox | null>(null);

  // Start true so a returning user sees 'syncing…' while init runs, never a
  // clickable auth link that will be wrong a second later.
  const [syncing, setSyncing] = useState(true);
  const [syncingError, setSyncingError] = useState<string | null>(null);

  const firstUpdate = useRef(true);

  const authenticate = useCallback(async (): Promise<string> => {
    const url = String(
      await dbxAuth.getAuthenticationUrl(
        redirectUrl,
        undefined,
        'code',
        'offline',
        undefined,
        undefined,
        true,
      ),
    );
    // The PKCE verifier must survive the redirect to Dropbox and back.
    localStorage.setItem(dboxCodeVerifier, dbxAuth.getCodeVerifier());
    return url;
  }, []);

  const loadBookmarks = useCallback(
    async (dbxInstance: Dropbox) => {
      setSyncing(true);
      try {
        const response = await dbxInstance.filesDownload({
          path: '/bookmarks.pb',
        });

        const remote = await parseFile(
          // TODO: correct typing
          (response.result as unknown as { fileBlob: File }).fileBlob,
        );

        // Union-merge: keep local-only items (offline edits) instead of
        // letting the remote download wipe them. Deletions made on another
        // device can resurrect — accepted tradeoff.
        onBookmarksLoad((prev) => mergeBookmarks(remote, prev));
      } catch (err) {
        console.error('Could not load bookmarks: ', err);
        setSyncingError(err.message);
      }
      setSyncing(false);
    },
    [onBookmarksLoad],
  );

  useEffect(() => {
    const init = async () => {
      // 1. Returning user with a refresh token: refresh the access token.
      const storedRefreshToken = localStorage.getItem(dboxRefreshToken);
      if (storedRefreshToken) {
        dbxAuth.setRefreshToken(storedRefreshToken);
        try {
          await dbxAuth.refreshAccessToken();
          const dbxInstance = new Dropbox({ auth: dbxAuth });
          setDbx(dbxInstance);
          loadBookmarks(dbxInstance);
          return;
        } catch (err) {
          console.error('Could not refresh access token:', err);
          localStorage.removeItem(dboxRefreshToken);
          // fall through: try the OAuth code in the URL, else re-authenticate
        }
      }

      // 2. Redirected back from Dropbox with an authorization code.
      const code = parseQueryString(window.location.search).code;
      if (code) {
        const codeVerifier = localStorage.getItem(dboxCodeVerifier);
        if (codeVerifier) {
          dbxAuth.setCodeVerifier(codeVerifier);
          try {
            const response = await dbxAuth.getAccessTokenFromCode(
              redirectUrl,
              code,
            );
            const refreshToken = (response.result as { refresh_token: string })
              .refresh_token;
            localStorage.setItem(dboxRefreshToken, refreshToken);
            dbxAuth.setRefreshToken(refreshToken);
            // Clean the code out of the URL.
            window.history.replaceState(null, '', window.location.pathname);
            const dbxInstance = new Dropbox({ auth: dbxAuth });
            setDbx(dbxInstance);
            loadBookmarks(dbxInstance);
            return;
          } catch (err) {
            console.error('Could not exchange authorization code:', err);
            setSyncingError(err.message);
          }
        }
        localStorage.removeItem(dboxCodeVerifier);
        setSyncing(false);
        return;
      }

      // 3. No session: start the PKCE auth flow.
      setSyncing(false);
      authenticate();
    };

    init();
  }, [authenticate, loadBookmarks]);

  const uploadFiles = useCallback(async () => {
    if (dbx) {
      if (firstUpdate.current) {
        firstUpdate.current = false;
        return;
      }

      setSyncing(true);
      try {
        await dbx.filesUpload({
          contents: new Blob([JSON.stringify(bookmarks)], {
            type: 'text/plain',
          }),
          path: '/bookmarks.pb',
          mode: { '.tag': 'overwrite' },
        });
      } catch (err) {
        console.error('Could not save bookmarks:');
        console.error(err);
        setSyncingError(err.message);
      }
      setSyncing(false);
    }
  }, [dbx, bookmarks]);

  useEffect(() => {
    uploadFiles();
  }, [uploadFiles]);

  const reauthenticate = useCallback(async () => {
    localStorage.removeItem(dboxRefreshToken);
    localStorage.removeItem(dboxCodeVerifier);
    window.location.href = await authenticate();
  }, [authenticate]);

  const logout = useCallback(() => {
    // Best-effort revoke; sign out locally regardless.
    if (dbx) {
      dbx.authTokenRevoke().catch(() => {});
    }
    localStorage.removeItem(dboxRefreshToken);
    localStorage.removeItem(dboxCodeVerifier);
    setDbx(null);
    setSyncingError(null);
  }, [dbx]);

  if (syncing) {
    return (
      <MainWrapper className={className} prefix>
        <span className="icon is-small">
          <i className="material-icons is-size-6">cloud_sync</i>
        </span>{' '}
        syncing…
      </MainWrapper>
    );
  }

  if (!dbx && !syncingError) {
    return (
      <MainWrapper className={className} prefix>
        <Button
          small
          onClick={() =>
            authenticate().then((url) => {
              window.location.href = url;
            })
          }
        >
          Connect
        </Button>
      </MainWrapper>
    );
  }

  if (syncingError) {
    return (
      <MainWrapper className={className}>
        <span
          className="icon is-small has-text-danger"
          data-tooltip={syncingError}
        >
          <i className="material-icons is-size-6">cloud_off</i>
        </span>{' '}
        <Button small onClick={reauthenticate}>
          Reauthenticate
        </Button>{' '}
        <Button small onClick={logout}>
          Log out
        </Button>
      </MainWrapper>
    );
  }

  return (
    <MainWrapper className={className} prefix>
      <span className="icon is-small has-text-success" data-tooltip="Synced">
        <i className="material-icons is-size-6">cloud_done</i>
      </span>{' '}
      synced{' '}
      <Button small onClick={logout}>
        Log out
      </Button>
    </MainWrapper>
  );
};
